// src/boot/csrfFetch.ts
let CSRF: string | null = null;
let inflight: Promise<string | null> | null = null;

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
let API_HOST: string | null = null;
try { API_HOST = API_BASE ? new URL(API_BASE).hostname : null; } catch { API_HOST = null; }

async function fetchCsrf(): Promise<string | null> {
  if (CSRF) return CSRF;
  if (!inflight) {
    inflight = (async () => {
      try {
        // If API_BASE is empty, fall back to relative — works with your Vercel rewrite
        const authMeUrl = API_BASE ? `${API_BASE}/auth/me` : '/auth/me';
        const r = await fetch(authMeUrl, { 
          credentials: 'include', 
          headers: { 'Content-Type': 'application/json' } 
        });
        const j = r.ok ? await r.json().catch(() => ({})) : {};
        CSRF = j?.csrf ?? null;
        return CSRF;
      } catch {
        return null;
      } finally {
        inflight = null;
      }
    })();
  }
  return inflight;
}

function needsCsrf(url: string, method: string) {
  const m = (method || 'GET').toUpperCase();
  if (!['POST','PUT','PATCH','DELETE'].includes(m)) return false;

  // Normalize to absolute URL
  const u = new URL(url, window.location.origin);

  // Never add CSRF to S3 or other third-party hosts (prevents CORS issues)
  const isThirdParty = /amazonaws\.com$/i.test(u.hostname) || /cloudfront\.net$/i.test(u.hostname);
  if (isThirdParty) return false;

  // Accept both app and api (same-site). In dev, also allow localhost and the configured API host
  const host = u.hostname.toLowerCase();
  const isSedraHost = /\.?sedrahq\.com$/i.test(host);
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  const isAppHost = host === window.location.hostname.toLowerCase();
  const isApiHost = API_HOST ? host === API_HOST.toLowerCase() : false;

  return isSedraHost || isLocal || isAppHost || isApiHost;
}

const origFetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
  const url = typeof input === 'string' ? input : (input as Request).url;
  const method = (init.method || 'GET').toUpperCase();

  let headers = new Headers(init.headers || (typeof input !== 'string' ? (input as Request).headers : undefined));

  if (needsCsrf(url, method)) {
    if (!headers.has('X-CSRF-Token')) {
      const token = await fetchCsrf();
      if (!token) console.debug('[csrfFetch] No CSRF available; request will likely 419.');
      headers.set('X-CSRF-Token', token ?? '');
    }
    // Ensure credentials ride along for any request we mark as needing CSRF
    init = { ...init, credentials: init.credentials || 'include' };
  }

  init = { ...init, headers };

  let res = await origFetch(input, init);

  // One retry if token rotated or first fetch raced token issuance
  if (needsCsrf(url, method) && (res.status === 401 || res.status === 419)) {
    CSRF = null;
    const fresh = await fetchCsrf();
    if (fresh) {
      const retryHeaders = new Headers(init.headers as HeadersInit);
      retryHeaders.set('X-CSRF-Token', fresh);
      res = await origFetch(input, { ...init, headers: retryHeaders });
    }
  }
  return res;
}; 